import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as UserIcon } from "../../../../assets/icons/fi-user.svg";
import { ReactComponent as LockIcon } from "../../../../assets/icons/lock.svg";
import InputField from "../../components/InputFeild";
import { userService } from "../../../../services/api/authenService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";

const LoginPage = () => {
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
    rememberMe: false,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusField, setFocusField] = useState(null);
  const [shakeKey, setShakeKey] = useState(0);

  const idRef = useRef(null);

  // Load "remember me"
  useEffect(() => {
    const savedIdentifier = localStorage.getItem("savedIdentifier");
    const rememberMe = localStorage.getItem("rememberMe") === "true";

    if (rememberMe && savedIdentifier) {
      setFormData((s) => ({
        ...s,
        identifier: savedIdentifier,
        password: "",
        rememberMe: true,
      }));
    }
  }, []);

  useEffect(() => {
    idRef.current?.focus?.();
  }, []);

  const iconColor = (field) => (focusField === field ? "#1D4ED8" : "#9E9E9E");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((s) => ({
      ...s,
      [name]:
        type === "checkbox"
          ? checked
          : name === "identifier"
            ? value.trim()
            : value,
    }));
    setErrors((s) => ({ ...s, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.identifier) {
      newErrors.identifier = "Vui lòng nhập tài khoản/MST/SĐT.";
    }
    if ((formData.password || "").length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự.";
    }
    return newErrors;
  };

  const canSubmit = useMemo(
    () => !!formData.identifier && !!formData.password && !isLoading,
    [formData.identifier, formData.password, isLoading],
  );

  const persistRememberMe = (remember) => {
    localStorage.setItem("rememberMe", remember ? "true" : "false");

    if (remember) {
      localStorage.setItem("savedIdentifier", formData.identifier);
    } else {
      localStorage.removeItem("savedIdentifier");
    }

    localStorage.removeItem("savedPassword");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setShakeKey((k) => k + 1);
      return;
    }

    setIsLoading(true);
    try {
      const response = await userService.log_in(
        formData.identifier,
        formData.password,
      );

      console.log("Login response:", response);

      // Kiểm tra response có đầy đủ thông tin
      if (response && response.token && response.account) {
        // Dispatch LOGIN để lưu vào cookies và state
        dispatch({
          type: "LOGIN",
          payload: {
            token: response.token,
            refreshToken: response.refreshToken,
            account: response.account,
          },
        });

        // Ghi nhớ tuỳ chọn "remember me"
        persistRememberMe(formData.rememberMe);

        toast.success("Đăng nhập thành công!");

        // Đợi một chút để toast hiển thị và cookies được set
        setTimeout(() => {
          navigate("/");
        }, 100);
      } else {
        console.error("Invalid response format:", response);
        setErrors({
          identifier: "Tài khoản đăng nhập không đúng.",
          password: "Mật khẩu không đúng.",
        });
        setShakeKey((k) => k + 1);
        toast.error("Tài khoản/mật khẩu không đúng. Vui lòng kiểm tra lại.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setShakeKey((k) => k + 1);

      const msg =
        error?.response?.data?.message ||
        "Đăng nhập thất bại, vui lòng kiểm tra lại.";

      toast.dismiss();
      toast.error(msg);

      // Set lỗi cho form
      setErrors({
        identifier: " ",
        password: " ",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFocus = (field) => setFocusField(field);
  const handleBlur = () => setFocusField(null);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <div className="flex-col items-center justify-center mb-3 lap:mb-6 space-y-1 hidden 3xl:flex">
        <img
          src="/logos/logo_vector.svg"
          alt="Web logo"
          className="size-[58px] 3xl:size-[72px]"
        />
        <h1 className="text-[#0267FF] font-sf-medium text-[36px] hidden 3xl:block">
          Sohuutritue
        </h1>
      </div>
      <div className="flex items-center mb-6">
        <img
          src="/logos/logo_vector.svg"
          alt="Web logo"
          className="size-[46px] 3xl:size-[72px] block 3xl:hidden"
        />
        <h1 className="text-2xl font-be-vietnam-semiBold font-bold">
          Đăng nhập
        </h1>
      </div>

      <form
        key={shakeKey}
        onSubmit={handleSubmit}
        className="w-full flex flex-col gap-4 animate-slide-in"
      >
        <InputField
          label="Tên tài khoản (Admin/SuperAdmin)"
          type="text"
          name="identifier"
          placeholder="Nhập số điện thoại / mã số thuế"
          IconComponent={UserIcon}
          value={formData.identifier}
          onChange={handleChange}
          error={errors.identifier}
          iconColor={iconColor("identifier")}
          onFocus={() => handleFocus("identifier")}
          onBlur={handleBlur}
          inputRef={idRef}
          autoComplete="username"
        />

        <InputField
          label="Mật khẩu"
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Mật khẩu"
          IconComponent={LockIcon}
          value={formData.password}
          onChange={handleChange}
          toggleVisibility={() => setShowPassword((prev) => !prev)}
          showPassword={showPassword}
          error={errors.password}
          iconColor={iconColor("password")}
          onFocus={() => handleFocus("password")}
          onBlur={handleBlur}
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="mr-2 accent-blue-500 font-sf-medium"
            />
            Nhớ tài khoản
          </label>
          <Link
            to="/forgot-password"
            className="text-sm font-sf-medium text-blue-500 hover:underline"
          >
            Quên mật khẩu
          </Link>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className={`group w-full mt-1 font-sf-medium text-white py-3 rounded-lg transition-all ${
            canSubmit
              ? "bg-blue-500 hover:bg-blue-600 active:scale-[0.99] shadow-lg shadow-blue-500/25"
              : "bg-blue-300/70 cursor-default"
          }`}
        >
          <span className="inline-flex items-center justify-center gap-2">
            {isLoading && <span className="spinner" aria-hidden />}
            {isLoading ? "Đang đăng nhập..." : "Đăng nhập"}
          </span>
        </button>
      </form>
    </div>
  );
};

export default LoginPage;
